import React from 'react';
import InputLabel from '@hobs/components/InputLabel';
import { WithErrorLabel } from '@hobs/components/FormError';
import { Suggest } from '@blueprintjs/select';
import ShortId from 'shortid';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withTranslation } from 'react-i18next/src/index';
import Styles from './Autocomplete.module.scss';

const DEMO_ITEMS = [
  {
    title: 'Vilnius',
    value: 'ilnius'
  },
  {
    title: 'Kaunas',
    value: 'kaunas'
  }
];

class Autocomplete extends React.PureComponent {
  constructor(props) {
    super(props);
    this.inputEl = React.createRef();

    const { defaultQuery, value } = this.props;

    this.state = {
      selectedItem: {
        title: defaultQuery,
        value: value ? value : ''
      }
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.defaultQuery !== this.props.defaultQuery ||
      prevProps.value !== this.props.value
    ) {
      this.setState({
        selectedItem: {
          title: this.props.defaultQuery,
          value: this.props.value ? this.props.value : ''
        }
      });
    }
  }

  handleChange = ({ target }) => {
    const { minLength, onQueryChange } = this.props;

    if (target.value.length >= minLength) {
      onQueryChange(target.value);
    }
  };

  onSelect = val => {
    const { onItemSelect } = this.props;

    this.setState(
      {
        query: val.title,
        selectedItem: {
          title: val.title,
          value: val.value
        }
      },
      () => {
        onItemSelect(val);
      }
    );
  };

  resetValue = () => {
    const { onItemSelect } = this.props;

    this.setState(
      {
        selectedItem: {
          title: '',
          value: ''
        }
      },
      () => {
        onItemSelect(null);
      }
    );
  };

  render() {
    const {
      label,
      placeholder,
      items,
      noResultsMessage,
      disabled,
      inputClass,
      indicationMessage,
      ...rest
    } = this.props;

    const { selectedItem } = this.state;

    return (
      <div
        className={classnames(Styles.autocomplete, {
          [inputClass]: inputClass
        })}
      >
        {label && (
          <InputLabel indicationMessage={indicationMessage}>{label}</InputLabel>
        )}
        <div className={Styles['input-wrapper']}>
          <Suggest
            inputValueRenderer={item => {
              return item.title;
            }}
            inputProps={{
              value: selectedItem.value,
              placeholder
            }}
            selectedItem={selectedItem}
            noResults={
              <div className={Styles['no-results']}>{noResultsMessage}</div>
            }
            itemRenderer={(item, { handleClick, modifiers }) => {
              return (
                <div
                  className={classnames(Styles.item, {
                    active: modifiers.active
                  })}
                  onClick={handleClick}
                  key={ShortId.generate()}
                >
                  {item.title}
                </div>
              );
            }}
            resetOnClose={false}
            resetOnQuery={true}
            resetOnSelect={true}
            popoverProps={{
              usePortal: false,
              minimal: true,
              portalClassName: classnames(Styles.popover)
            }}
            filterable={false}
            initialContent={null}
            items={items}
            disabled={disabled}
            {...rest}
            onItemSelect={this.onSelect}
            placeholder={placeholder}
          />
          {selectedItem.title && !disabled && (
            <button
              className={Styles.reset}
              type="button"
              onClick={this.resetValue}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    );
  }
}

Autocomplete.defaultProps = {
  items: DEMO_ITEMS,
  minLength: 1,
  noResultsMessage: 'No results',
  onQueryChange: () => {},
  defaultQuery: '',
  onItemSelect: () => {},
  disabled: false
};

Autocomplete.propTypes = {
  items: PropTypes.array,
  minLength: PropTypes.number,
  label: PropTypes.string,
  inputClass: PropTypes.string,
  defaultQuery: PropTypes.string,
  placeholder: PropTypes.string,
  noResultsMessage: PropTypes.string,
  onQueryChange: PropTypes.func,
  onItemSelect: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  t: PropTypes.func,
  disabled: PropTypes.bool,
  indicationMessage: PropTypes.object
};

export default withTranslation()(WithErrorLabel(Autocomplete));
